/**
 * Process streamed HTML content to hide incomplete markdown link URLs during streaming.
 *
 * During streaming, markdown links come in incrementally:
 * - Incomplete: `[Massachusetts Online Voter Registration System](https://www.sec.state`
 * - Complete: `[Massachusetts Online Voter Registration System](https://www.sec.state.ma.us/ovr/)`
 *
 * This function detects incomplete markdown patterns and:
 * - Shows the link title text immediately
 * - Hides the incomplete URL portion
 * - Once complete, mistune converts the full markdown to a proper <a> tag
 *
 * This prevents the jarring effect of showing raw markdown syntax during streaming.
 *
 * @param html - The HTML content from the stream (output of mistune.html())
 * @returns Processed HTML with incomplete markdown URLs hidden
 */
export const processResponseStream = (streamingText: string): string => {
  if (!streamingText) return streamingText

  // Hide incomplete markdown link patterns that haven't been converted to HTML yet
  // Pattern: `[text](incomplete-url` at the end of the string
  const incompleteMarkdownPattern = /\[([^\]]+)\]\(([^)]+)$/

  if (incompleteMarkdownPattern.test(streamingText)) {
    // Find the start of the incomplete markdown link and show title, hide URL
    const match = streamingText.match(incompleteMarkdownPattern)
    if (match && match.index !== undefined) {
      const beforeLink = streamingText.substring(0, match.index)
      const linkTitle = match[1] // The text inside [...]
      const incompleteUrl = match[2] // The incomplete URL inside (...)

      // Show the title text, but hide the incomplete URL part
      return `${beforeLink}${linkTitle}<span style="display: none;" data-incomplete-markdown="true">](${incompleteUrl}</span>`
    }
  }

  return streamingText
}
